import { createContext, useContext, useEffect, useState } from "react";
import { useCheckpoints, useDynamicModules, useUserDetail, useUserPermissions } from "../services/hooks";
import { useLocation } from "react-router-dom";
import {
	DashboardIcon,
	AnalyticsIcon,
	DeviceIcon,
	TagIcon,
	UsersIcon,
	CompanyIcon,
	ProductIcon,
	ProductCategoriesIcon,
	SupplierIcon,
	VehicleIcon,
	TagMappingIcon,
	RoleIcon,
	GateOutIcon,
	GateInIcon,
	AccessIcon,
	BayIcon,
	DummyIcon,
	ReportIcon,
	WorkFlowIcon,
	ManageTripIcon,
	ParkingIcon,
	WeighbridgeIcon,
	ApprovalIcon,
	CheckpointTypeIcon,
	DynamicFormIcon,
	DynamicModuleIcon,
	BallotIcon,
	DriverIcon,
	ApiIcon,
	SettingsInputAntennaIcon,
	PlantIcon,
	VehicleMovement
} from "../services/icons";
import { capitalizeCamelCase, removeSpecialChars } from "../services/functions";
import { usePlantContext } from "./plantContext";

const SideBarItemsContext = createContext();
export function SideBarItemsProvider(props) {
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions } = useUserPermissions();
	const { pathname } = useLocation();

	const [search, setSearch] = useState("");

	const checkpointItems = useCheckpointItems();
	const dynamicModuleItems = useDynamicModuleItems();
	let sidebarItems = [
		{
			title: "Dashboard",
			Icon: DashboardIcon,
			link: "/dashboard_old",
			permission: userPermissions?.getDashboard?.value,
			current: ["/dashboard_old"].includes(pathname),
			children: [],
			class: "dashboard-module",
			description: "You can view analytics and various stats here",
		},
		{
			title: "Dashboard",
			Icon: DashboardIcon,
			link: "/dashboard",
			permission: userPermissions?.getDashboardV2?.value || isSuperAdmin,
			current: ["/dashboard"].includes(pathname),
			children: [],
			class: "dashboard-module",
			description: "You can view analytics and various stats here",
		},
		{
			title: "Dashboard",
			Icon: VehicleMovement,
			link: "/vehicle-movement-stats",
			permission: userPermissions?.viewVehicleMovementStats?.value,
			current: ["/vehicle-movement-stats"].includes(pathname),
			children: [],
			class: "vehicle-movement-stats-module",
			description: "You can view vehicle movement stats here",
		},
		{
			title: "All Trips",
			Icon: ManageTripIcon,
			link: "/allTrips",
			permission: userPermissions?.getAllTrips?.value,
			current: pathname.includes("/allTrips"),
			children: [],
			class: "all-trip-module",
			description: "You can see your all trips here.",
		},
		{
			title: "Device Map",
			Icon: DashboardIcon,
			link: "/device-map",
			permission: userPermissions?.viewDeviceMap?.value || isSuperAdmin,
			current: pathname.includes("/device-map"),
			children: [],
			class: "devive-map-module",
			description: "You can view your devices and their status here",
		},
		{
			title: "Analytics",
			Icon: AnalyticsIcon,
			link: "/analytics",
			permission: userPermissions?.getAnalytics?.value || isSuperAdmin,
			current: ["/analytics"].includes(pathname),
			children: [],
			class: "analytics-module",
			description: "You can view analytics and various stats here",
		},
		{
			title: "Api Logs",
			Icon: ApiIcon,
			link: "/third-party-logs",
			permission: isSuperAdmin,
			current: ["/third-party-logs"].includes(pathname),
			children: [],
			class: "third-party-module",
			description: "You can view third party API logs here",
		},
		{
			title: "Devices",
			Icon: DeviceIcon,
			link: "/devices",
			permission: userPermissions?.getDevice?.value || isSuperAdmin,
			current: ["/devices", "/devices/add-device"].includes(pathname),
			children: [],
			class: "device-module",
			description: "You can manage different Devices here.",
		},
		{
			title: "Tags",
			Icon: TagIcon,
			link: "/tags",
			permission: userPermissions?.getTag?.value || isSuperAdmin,
			current: ["/tags"].includes(pathname),
			children: [],
			class: "tag-module",
			description: "You can manage RFID Tags here.",
		},
		{
			title: "Stray tags",
			Icon: TagMappingIcon,
			link: "/stray-tags",
			permission: userPermissions?.accessStrayTags?.value || isSuperAdmin,
			current: ["/stray-tags"].includes(pathname),
			children: [],
			class: "unregister-tag-module",
			description: "Find stray tags scanned by RFID devices here",
		},
		{
			title: "Approval Configuration",
			Icon: TagMappingIcon,
			link: "/approval-configuration",
			permission: userPermissions?.getApprovalConfig?.value || isSuperAdmin,
			current: ["/approval-configuration"].includes(pathname),
			children: [],
			class: "approval-config-module",
			description: "Here you manage the approval configurations.",
		},
		{
			title: "Users",
			Icon: UsersIcon,
			link: "/users",
			permission: userPermissions?.getUsers?.value || isSuperAdmin,
			current: ["/users"].includes(pathname),
			children: [],
			class: "users-module",
			description: "You can add your users here, giving them roles and relevant permissions",
		},
		{
			title: "Roles",
			Icon: RoleIcon,
			link: "/roles",
			permission: userPermissions?.getUserRoles?.value || isSuperAdmin,
			current: ["/roles"].includes(pathname),
			children: [],
			class: "role-module",
			description: "You can manage roles for your users here.",
		},
		{
			title: "Companies",
			Icon: CompanyIcon,
			link: "/companies",
			permission: userPermissions?.getOrganizations?.value || isSuperAdmin,
			current: ["/companies"].includes(pathname),
			children: [],
			class: "company-module",
			description: "You can manage your organizations here.",
		},
		{
			title: "Warehouses",
			Icon: PlantIcon,
			link: "/plants",
			permission: userPermissions?.getPlants?.value || isSuperAdmin,
			current: ["/plant"].includes(pathname),
			children: [],
			class: "plant-module",
			description: "You can manage your plants here",
		},
		{
			title: "Product Categories",
			Icon: ProductCategoriesIcon,
			link: "/product-categories",
			permission: userPermissions?.getProductCategory?.value || isSuperAdmin,
			current: ["/product-categories"].includes(pathname),
			children: [],
			class: "product-categories-module",
			description: "Here you can manage your Product Categories.",
		},
		{
			title: "Products",
			Icon: ProductIcon,
			link: "/products",
			permission: userPermissions?.getProduct?.value || isSuperAdmin,
			current: ["/products"].includes(pathname),
			children: [],
			class: "product-module",
			description: "Here you can manage your Products.",
		},
		{
			title: "Suppliers",
			Icon: SupplierIcon,
			link: "/suppliers",
			permission: userPermissions?.getSupplier?.value || isSuperAdmin,
			current: ["/suppliers"].includes(pathname),
			children: [],
			class: "supplier-module",
			description: "Here you can manage your Suppliers.",
		},
		{
			title: "Reports",
			Icon: ReportIcon,
			link: "/reports",
			permission: userPermissions?.viewReport?.value || isSuperAdmin,
			current: pathname.includes("/reports"),
			children: [],
			class: "report-module",
			description: "You can view your reports here.",
		},
		{
			title: "Driver Master",
			Icon: DriverIcon,
			link: "/driver-master",
			permission: userPermissions?.getDriver?.value || isSuperAdmin,
			current: pathname.includes("/driver-master"),
			children: [],
			class: "driver-module",
			description: "You can manage your drivers here.",
		},
		{
			title: "Vehicle Master",
			Icon: VehicleIcon,
			link: "/vehicle-master",
			permission: userPermissions?.getVehicle?.value || isSuperAdmin,
			current: pathname.includes("/vehicle-master"),
			children: [],
			class: "vehicle-module",
			description: "You can manage your vehicles here.",
		},
		{
			title: "Checkpoints",
			Icon: ReportIcon,
			link: "/checkpoints",
			permission: userPermissions?.getCheckpoint?.value || isSuperAdmin,
			current: pathname == "/checkpoints" || pathname == "/checkpoints/", 
			children: [],
			class: "checkpoints-master-module",
			description: "You can manage your checkpoints here.",
		},
		{
			title: "Checkpoint Types",
			Icon: CheckpointTypeIcon,
			link: "/checkpoint-types",
			permission: userPermissions?.getCheckpointType?.value || isSuperAdmin,
			current: pathname.includes("/checkpoint-types"),
			children: [],
			class: "checkpoint-type-module",
			description: "You can manage checkpoint types here.",
		},
		{
			title: "WorkFlow",
			Icon: WorkFlowIcon,
			link: "/workflow",
			permission: userPermissions?.manageWorkflow?.value || isSuperAdmin,
			current: pathname.includes("/workflow"),
			children: [],
			class: "workflow-module",
			description: "You can manage your workflows here.",
		},
		{
			title: "Manage Trips",
			Icon: ManageTripIcon,
			link: "/manage-trip",
			permission: userPermissions?.manageTrip?.value,
			current: pathname.includes("/manage-trip"),
			children: [],
			class: "manage-trip-module",
			description: "You can manage your trips here.",
		},
		
		{
			title: "Approval Management",
			Icon: ApprovalIcon,
			link: "/approval-management",
			permission: userDetail?.userRole == "bayOperator" || userPermissions?.manageApprovals?.value,
			current: pathname.includes("/approval-management"),
			children: [],
			class: "approval-management-module",
			description: "You can manage your approvals here.",
		},
		{
			title: "Rule Management",
			Icon: SettingsInputAntennaIcon,
			link: "/rule-management",
			permission: isSuperAdmin,
			current: pathname.includes("/rule-management"),
			children: [],
			class: "rule-management-module",
			description: "You can manage your rules here.",
		},
		{
			title: "Resources",
			Icon: ReportIcon,
			link: "/resources",
			permission: userPermissions?.getResource?.value || isSuperAdmin,
			current: pathname.includes("/resources"),
			children: [],
			class: "resources-module",
			description: "You can see all the resources here.",
		},
		{
			title: "Dynamic Form",
			Icon: DynamicFormIcon,
			link: "/templates",
			permission: isSuperAdmin,
			current: pathname.includes("/templates"),
			children: [],
			class: "dynamic-form-module",
			description: "You can manage your dynamic forms here.",
		},
		{
			title: "Dynamic Module",
			Icon: DynamicModuleIcon,
			link: "/dynamic-module",
			permission: isSuperAdmin || userPermissions?.manageModule?.value,
			current: pathname.includes("/dynamic-module"),
			children: [],
			class: "dynamic-module",
			description: "You can manage your dynamic modules here",
		},
		...dynamicModuleItems,
		...(userPermissions?.hideCheckpoints?.value ? [] : checkpointItems),
	]
		.filter((_) => _.permission)
		.map((_) => ({ ..._, children: _.children.filter((_) => _.title.toLowerCase().includes(search.trim().toLowerCase())) }))
		.filter((_) => {
			const _search = search.trim().toLowerCase();
			const childMatch = !!_.children.find((_) => _.title.toLowerCase().includes(_search));
			const parentMatch = _.title.toLowerCase().includes(_search);
			return childMatch || parentMatch;
		});
	// console.log(items);
	return <SideBarItemsContext.Provider value={{ sidebarItems, search, setSearch }} {...props} />;
}

export function useSidebarItems() {
	const context = useContext(SideBarItemsContext);
	if (!context) {
		throw new Error("useSideItems must be within a SidebarItemsProvider");
	}
	return context;
}

function useCheckpointItems() {
	const { pathname } = useLocation();
	const { checkpoints, getCheckpoints } = useCheckpoints();
	const checkpointItems = categoriesCheckpointItems(checkpoints);
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { plantId } = usePlantContext();
	useEffect(() => {
		if (!userDetail || isSuperAdmin) return;
		getCheckpoints({ orgId: userDetail?.orgId, plantIds: plantId });
	}, [userDetail, plantId]);

	if (isSuperAdmin) return [];

	const checkpointMenu = Object.keys(checkpointItems).map((ele, index) => {
		const iconMap = {
			gateOut: GateOutIcon,
			gateIn: GateInIcon,
			access: AccessIcon,
			bay: BayIcon,
			parking: ParkingIcon,
			weighbridge: WeighbridgeIcon,
		};
		if (checkpointItems[ele].length > 0) {
			return {
				title: capitalizeCamelCase(removeSpecialChars(ele)),
				Icon: iconMap[ele] || DummyIcon,
				link: null,
				permission: true,
				current: null,
				class: "checkpoint-module",
				description: "You can manage vehicle entries at your predefined locations here.",
				children: checkpointItems[ele].map((_) => {
					const iconMap = {
						gateOut: GateOutIcon,
						gateIn: GateInIcon,
						access: AccessIcon,
						bay: BayIcon,
						parking: ParkingIcon,
						weighbridge: WeighbridgeIcon,
					};
					return {
						title: _.displayName,
						Icon: iconMap[_.checkpointType] || DummyIcon,
						link: `/checkpoints/${_.checkpointType}/${_.checkpointNumber}`,
						permission: true,
						current: [`/checkpoints/${_.checkpointType}`].includes(pathname),
						children: [],
					};
				}),
			};
		} else {
			null;
		}
	});
	// console.log("checkpointMenu", checkpointMenu)
	return checkpointMenu || [];
}
function categoriesCheckpointItems(data) {
	// Define an empty object to store the categorized data
	const categorizedData = {};

	// Iterate through the array and group objects by checkpointType
	data.forEach((item) => {
		const { checkpointType } = item;
		if (!categorizedData[checkpointType]) {
			// If the category doesn't exist, create an array for it
			categorizedData[checkpointType] = [item];
		} else {
			// If the category already exists, push the item to the existing array
			categorizedData[checkpointType].push(item);
		}
	});

	return categorizedData;
}
function useDynamicModuleItems() {
	const { pathname } = useLocation();
	const { isSuperAdmin } = useUserDetail();
	const { userPermissions } = useUserPermissions();
	const { dynamicModules } = useDynamicModules({});
	return (
		dynamicModules.map((_) => {
			const canGet = isSuperAdmin || !!userPermissions[`module-get${_.moduleName.replaceAll(" ", "")}`]?.value;
			return {
				title: _.moduleName,
				Icon: BallotIcon,
				link: `/module/${_.moduleName.replaceAll(" ", "")}`,
				state: { data: _ },
				permission: canGet,
				current: [`/module/${_.moduleName}`].includes(pathname),
				children: [],
				class: `dynamic-module-${_.moduleName}`,
				description: `You can manage ${_.moduleName} here.`,
			};
		}) || []
	);
}
